package com.merkost.handpass.admin.components

import androidx.compose.runtime.Composable
import com.merkost.handpass.admin.contract.AsyncSection
import com.merkost.handpass.api.dto.admin.AdminNote
import com.merkost.handpass.api.dto.admin.AuditEntry
import com.merkost.handpass.sharedweb.theme.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun AdminNotesSection(
    draft: String,
    notes: AsyncSection<List<AdminNote>>,
    onDraftChange: (String) -> Unit,
    onSubmit: () -> Unit,
    onRequestDelete: (String) -> Unit,
    onRetry: (() -> Unit)? = null,
) {
    AdminCard {
        AdminInput(
            value = draft,
            onChange = onDraftChange,
            placeholder = "Add a note (visible to other admins)…",
        )
        Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.FlexEnd); property("margin-top", "${HandpassSpacing.SM}") } }) {
            AdminButton(
                label = "Add note",
                onClick = onSubmit,
                variant = AdminButtonVariant.Primary,
                size = AdminButtonSize.SM,
                enabled = draft.isNotBlank(),
            )
        }
    }

    when (notes) {
        AsyncSection.Loading -> AdminCard { AdminSkeletonTable(rows = 3) }
        is AsyncSection.Failed -> AdminEmptyState(
            title = "Failed to load notes",
            description = notes.message,
            actionLabel = onRetry?.let { "Retry" },
            onAction = onRetry,
        )
        is AsyncSection.Loaded -> {
            if (notes.value.isEmpty()) AdminEmptyState(title = "No notes yet")
            else notes.value.forEach { note -> AdminNoteRow(note = note, onRequestDelete = onRequestDelete) }
        }
    }
}

@Composable
private fun AdminNoteRow(note: AdminNote, onRequestDelete: (String) -> Unit) {
    AdminCard {
        Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.FlexStart) } }) {
            Div {
                Div({ style { fontSize(HandpassTypography.Body); color(OnSurface.value()); property("white-space", "pre-wrap") } }) { Text(note.body) }
                Div({ style { fontSize(HandpassTypography.Caption); color(OnSurfaceFaint.value()); property("margin-top", "${HandpassSpacing.XS}") } }) {
                    Text("by ${note.authorName ?: "?"} · ${note.createdAt.take(10)}")
                }
            }
            AdminButton(label = "Delete", onClick = { onRequestDelete(note.id) }, variant = AdminButtonVariant.Ghost, size = AdminButtonSize.SM)
        }
    }
}

@Composable
fun AdminViewedBySection(
    audit: AsyncSection<List<AuditEntry>>,
    onRetry: (() -> Unit)? = null,
) {
    when (audit) {
        AsyncSection.Loading -> AdminCard { AdminSkeletonTable(rows = 3) }
        is AsyncSection.Failed -> AdminEmptyState(
            title = "Failed to load audit",
            description = audit.message,
            actionLabel = onRetry?.let { "Retry" },
            onAction = onRetry,
        )
        is AsyncSection.Loaded -> {
            if (audit.value.isEmpty()) AdminEmptyState(title = "No views recorded yet")
            else audit.value.forEach { entry ->
                AdminCard {
                    Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(HandpassSpacing.MD) } }) {
                        AdminAvatar(name = entry.viewerName, url = entry.viewerAvatarUrl, size = AdminAvatarSize.SM)
                        Div {
                            Div({ style { fontWeight(500); color(OnSurface.value()) } }) { Text(entry.viewerName ?: "(unknown)") }
                            Div({ style { fontSize(HandpassTypography.Caption); color(OnSurfaceFaint.value()) } }) { Text(entry.viewedAt.take(16).replace("T", " ")) }
                        }
                    }
                }
            }
        }
    }
}
